import { ArrowLeft, FileText, Video } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useTrainingStore,
  usePremisesStore,
  useRegionStore,
} from "../../store/store";
import { Training, Test, Trainee, QuizQuestion } from "../../types/api";
import Modal from "../../components/modal/Modal";
import images from "../../assets/images";
import { getS3SignedUrl } from "../../utils/s3Client";
import axiosInstance from "../../common/axiosInstance";
import QuizForm from "./QuizForm";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import { XIcon } from "lucide-react";
import { useTrainingModule } from "./TrainingModuleContext";

interface TrainingViewProps {
  mode: "view";
}

const isVideoFile = (fileName: string): boolean => {
  const videoExtensions = [
    ".mp4",
    ".avi",
    ".mov",
    ".wmv",
    ".flv",
    ".webm",
    ".mkv",
    ".m4v",
  ];
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf("."));
  return videoExtensions.includes(ext);
};

// Legacy trainings only ever persisted total minutes, so synthesize a
// range from a default 09:00 start instead of showing a bare minute count.
const DEFAULT_TRAINING_START_TIME = "09:00";
const deriveTrainingTimeRange = (
  duration: string | number | undefined,
  startTime?: string,
  endTime?: string,
): { start: string; end: string } => {
  if (startTime && endTime) return { start: startTime, end: endTime };
  const total =
    typeof duration === "string" ? parseInt(duration, 10) : duration;
  const [h, m] = DEFAULT_TRAINING_START_TIME.split(":").map(Number);
  const endMinutes =
    (((h * 60 + m + (total && !isNaN(total) ? total : 0)) % 1440) + 1440) %
    1440;
  const end = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(
    endMinutes % 60,
  ).padStart(2, "0")}`;
  return { start: DEFAULT_TRAINING_START_TIME, end };
};

const TrainingView: React.FC<TrainingViewProps> = ({ mode }) => {
  const { id } = useParams<{ id?: string }>();
  const { moduleType, validCategories, defaultCategory, paths } =
    useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { fetchTrainingById } = useTrainingStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const [training, setTraining] = useState<Training | null>(null);
  const [tests, setTests] = useState<Test[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(1);
  const listPerPage = 5;
  const [departmentMap, setDepartmentMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [selectedTestIndex, setSelectedTestIndex] = useState(0);
  const [answers, setAnswers] = useState<
    { questionId: number; selectedOption: number }[]
  >([]);
  const [fileUrl, setFileUrl] = useState<{ url: string; name: string }>({
    url: "",
    name: "",
  });

  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  useEffect(() => {
    const fetchTrainingAndTests = async () => {
      if (!id) {
        setError("No training ID provided");
        return;
      }
      try {
        const trainingResponse = await fetchTrainingById(id);
        if (
          !trainingResponse ||
          !("data" in trainingResponse) ||
          !trainingResponse.data
        ) {
          setError("Training data is incomplete");
          return;
        }
        const responseData = trainingResponse as unknown as {
          success: boolean;
          data: Training;
        };
        const matchesModule = isFixedCategory
          ? responseData.data.moduleType === moduleType ||
            responseData.data.category === defaultCategory
          : responseData.data.moduleType === moduleType ||
            validCategories.includes(responseData.data.category);
        if (!matchesModule) {
          setError(`Training not found in ${defaultCategory} module`);
          return;
        }
        setTraining({
          ...responseData.data,
          ...(isFixedCategory
            ? { category: defaultCategory, moduleType }
            : {}),
          instructor: Array.isArray(responseData.data.instructor)
            ? responseData.data.instructor
            : [],
          files: Array.isArray(responseData.data.files)
            ? responseData.data.files
            : [],
          date:
            responseData.data.date &&
            !isNaN(new Date(responseData.data.date).getTime())
              ? responseData.data.date
              : new Date().toISOString(),
        });
        const departmentIds =
          responseData.data.trainees
            ?.map((trainee: Trainee) => trainee.department)
            .filter((dept): dept is string => !!dept) || [];
        const uniqueDepartmentIds = Array.from(new Set(departmentIds));
        if (uniqueDepartmentIds.length > 0) {
          const deptResponse = await axiosInstance.get(`/api/departments`, {
            params: { ids: uniqueDepartmentIds.join(",") },
          });
          const departments = deptResponse.data.data || [];
          const deptMap = new Map<string, string>(
            departments.map(
              (dept: {
                _id: string | { $oid: string };
                departmentName: string;
              }) => [
                typeof dept._id === "object" &&
                dept._id !== null &&
                "$oid" in dept._id
                  ? String(dept._id.$oid)
                  : String(dept._id),
                dept.departmentName || "Unknown",
              ],
            ),
          );
          setDepartmentMap(deptMap);
        }
        let testData = Array.isArray(responseData.data.tests)
          ? responseData.data.tests
          : [];
        if (
          testData.length === 0 &&
          Array.isArray(responseData.data.testsData)
        ) {
          testData = responseData.data.testsData;
        }
        const mappedTests: Test[] = testData.map((test: any) => ({
          id: test._id || test.id,
          trainingId: test.trainingId,
          topic: test.topic,
          testCategory: test.testCategory,
          date: test.date,
          duration: test.duration,
          quiz: test.quiz,
          status: test.status,
          tenantId: test.tenantId,
          createdAt: test.createdAt,
          updatedAt: test.updatedAt,
          results: test.results,
        }));
        setTests(mappedTests);

        // Set pre-decided correct answers based on quiz data
        const preDefinedAnswers: {
          questionId: number;
          selectedOption: number;
        }[] = [];
        mappedTests.forEach((test) => {
          test.quiz?.forEach((question: QuizQuestion) => {
            const correctOptionIndex = question.options.findIndex(
              (option) => option.isCorrect,
            );
            if (correctOptionIndex !== -1) {
              preDefinedAnswers.push({
                questionId: question.id,
                selectedOption: correctOptionIndex,
              });
            }
          });
        });
        setAnswers(preDefinedAnswers);
      } catch (err) {
        setError("Failed to load training or test data");
        console.error("Error fetching data:", err);
      }
    };
    fetchTrainingAndTests();
  }, [id, fetchTrainingById, moduleType, isFixedCategory, defaultCategory]);

  const handleViewFile = async (fileUrl: string) => {
    try {
      // Extract the S3 key from the full URL (remove bucket and domain)
      const key = fileUrl.replace(
        "https://dev-kc-repo.s3.ap-south-1.amazonaws.com/",
        "",
      );
      // Determine MIME type based on file extension
      let mimeType = "application/octet-stream"; // Default
      const fileExtension = key.split(".").pop()?.toLowerCase();
      if (fileExtension) {
        const mimeTypes: { [key: string]: string } = {
          pdf: "application/pdf",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          txt: "text/plain",
          jpg: "image/jpeg",
          png: "image/png",
        };
        mimeType = mimeTypes[fileExtension] || mimeType;
      }

      const signedUrl = await getS3SignedUrl(key, mimeType);
      setFileUrl({ url: signedUrl, name: key.split("/").pop() || "document" });
    } catch (err) {
      console.error("Error generating signed URL:", err);
      setError("Failed to load file. Check S3 permissions or file existence.");
    }
  };

  const handleCloseFile = () => {
    setFileUrl({ url: "", name: "" });
  };

  const handleNext = () => {
    if (currentSlide < 3) {
      setCurrentSlide(currentSlide + 1);
      setError(null);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 1) {
      setCurrentSlide(currentSlide - 1);
      setError(null);
    }
  };

  const indexOfLastItem = currentPage * listPerPage;
  const indexOfFirstItem = indexOfLastItem - listPerPage;
  const currentTrainees =
    training?.trainees?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages =
    Math.ceil((training?.trainees?.length || 0) / listPerPage) || 1;

  const progress = currentSlide === 1 ? 50 : currentSlide === 2 ? 75 : 100;

  const trainingPremises = training?.premises || [];
  const premisesDisplay =
    trainingPremises.length === 0
      ? "All Premises"
      : trainingPremises
          .map(
            (id) =>
              allPremises.find((p) => p._id === id)?.premisesName || id,
          )
          .join(", ");

  const trainingRegions = training?.regions || [];
  const regionsDisplay =
    trainingRegions.length === 0
      ? "All Regions"
      : trainingRegions
          .map(
            (id) => regions.find((r) => r._id === id)?.regionName || id,
          )
          .join(", ");

  const instructorsDisplay =
    training && training.instructor.length > 0
      ? training.instructor
          .map((inst) => `${inst.trainerName} (${inst.trainerEmployeeId})`)
          .join(", ")
      : "No instructor assigned";

  if (!training) {
    return (
      <div className="px-2 py-4 mt-20">
        <div className="flex justify-center items-center">
          <h2 className="text-2xl font-semibold text-blue-800">
            View Training
          </h2>
          <Link to={paths.dashboard}>
            <button className="flex items-center gap-2 bg-[#61748e] text-white px-4 py-2 rounded-full ml-4">
              <ArrowLeft /> Back
            </button>
          </Link>
        </div>
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4">
          <p>{error || "Loading training data..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-4 mt-20">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-blue-800">View Training</h2>
        <Link to={paths.dashboard}>
          <button className="flex items-center gap-2 bg-[#61748e] text-white px-4 py-2 rounded-full">
            <ArrowLeft /> Back
          </button>
        </Link>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 my-4">
        <div
          className="bg-[#0fa9a6] h-2.5 rounded-full"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}

      {currentSlide === 1 && (
        <div className="bg-white p-6 mt-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Training Details
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">Topic</label>
              <input
                type="text"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={training.topic}
                disabled
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">Category</label>
              <input
                type="text"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={training.category}
                disabled
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">
                Training Date
              </label>
              <input
                type="date"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={
                  training.date
                    ? new Date(training.date).toISOString().split("T")[0]
                    : ""
                }
                disabled
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">
                Training Time
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                  value={
                    deriveTrainingTimeRange(
                      training.duration,
                      training.startTime,
                      training.endTime,
                    ).start
                  }
                  disabled
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                  value={
                    deriveTrainingTimeRange(
                      training.duration,
                      training.startTime,
                      training.endTime,
                    ).end
                  }
                  disabled
                />
              </div>
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">
                Instructor(s)
              </label>
              <input
                type="text"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={instructorsDisplay}
                disabled
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">Method</label>
              <input
                type="text"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={training.method || ""}
                disabled
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">Region(s)</label>
              <input
                type="text"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={regionsDisplay}
                disabled
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">
                Premises
              </label>
              <input
                type="text"
                className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                value={premisesDisplay}
                disabled
              />
            </div>
          </div>
          <div className="flex flex-col mt-6">
            <label className="text-gray-500 font-semibold">
              Attached Files
            </label>
            <div className="border-2 border-[#a5b7cf] p-4 rounded-lg text-center">
              <p className="text-gray-500 font-semibold">Attached Files</p>
              <div className="flex gap-4 mt-4 flex-wrap">
                {Array.isArray(training.files) && training.files.length > 0 ? (
                  training.files.map((file, index) => {
                    const fileName = file.split("/").pop() || "";
                    const isVideo = isVideoFile(fileName);
                    return (
                      <div
                        key={index}
                        className="flex flex-col items-center bg-gray-100 p-3 rounded-md w-24 relative group cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleViewFile(file)}
                        title={fileName}
                      >
                        {isVideo ? (
                          <Video size={32} className="text-red-500" />
                        ) : (
                          <FileText size={32} className="text-blue-500" />
                        )}
                        <p className="text-xs text-gray-600 truncate w-full text-center">
                          {fileName}
                        </p>
                        {isVideo && (
                          <span className="text-xs text-red-500 font-semibold mt-1">
                            Video
                          </span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-sm">No files attached</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {currentSlide === 2 && (
        <div className="bg-white p-6 mt-6 rounded shadow-md">
          <h2 className="text-xl font-semibold mb-4">Trainee Attendance</h2>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-100">
                  <th className="border p-2">Name</th>
                  <th className="border p-2">Employee ID</th>
                  <th className="border p-2">Department</th>
                  <th className="border p-2">Role</th>
                </tr>
              </thead>
              <tbody>
                {currentTrainees.length > 0 ? (
                  currentTrainees.map((trainee, index) => (
                    <tr key={trainee.traineeId || index} className="border">
                      <td className="border p-2">{trainee.name}</td>
                      <td className="border p-2">
                        {trainee.traineeEmployeeId}
                      </td>
                      <td className="border p-2">
                        {departmentMap.get(trainee.department) || "Unknown"}
                      </td>
                      <td className="border p-2">
                        {trainee.roleName ||
                          (typeof trainee.role === "object" &&
                            trainee.role?.$oid) ||
                          String(trainee.role) ||
                          "Unknown"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center p-4 text-gray-500">
                      No trainees selected
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {currentSlide === 3 && (
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Test Details</h1>
          </div>
          <div className="bg-white p-8 mt-6 rounded-lg shadow-md">
            <div className="grid grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-gray-500 font-semibold mb-1">
                  Test Category *
                </label>
                <select
                  className="border-2 border-[#a5b7cf] p-3 rounded w-full"
                  value={tests[selectedTestIndex]?.testCategory || "Pre-Test"}
                  onChange={(e) => {
                    const index = tests.findIndex(
                      (t) => t.testCategory === e.target.value,
                    );
                    setSelectedTestIndex(index >= 0 ? index : 0);
                  }}
                >
                  {tests.map((t, index) => (
                    <option key={index} value={t.testCategory}>
                      {t.testCategory}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-gray-500 font-semibold mb-1">
                  Test Date *
                </label>
                <input
                  type="date"
                  className="border-2 border-[#a5b7cf] p-3 rounded w-full bg-gray-100 cursor-not-allowed"
                  value={
                    tests[selectedTestIndex]?.date
                      ? new Date(tests[selectedTestIndex].date)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  disabled
                />
              </div>
              <div className="flex flex-col">
                <label className="text-gray-500 font-semibold mb-1">
                  Test Duration (in mins) *
                </label>
                <input
                  type="text"
                  className="border-2 border-[#a5b7cf] p-3 rounded w-full bg-gray-100 cursor-not-allowed"
                  value={tests[selectedTestIndex]?.duration || ""}
                  disabled
                />
              </div>
            </div>
            <QuizForm
              questions={tests[selectedTestIndex]?.quiz || []}
              mode="view"
              onOptionSelect={() => {}}
              selectedAnswers={answers}
            />
            <style>
              {`
                input[type="radio"]:checked {
                  background-color: #3b82f6; /* Blue for correct answer */
                }
                input[type="radio"]:checked::before {
                  background-color: #3b82f6; /* Ensure the dot is blue */
                }
              `}
            </style>
          </div>
        </div>
      )}

      <div className="flex bg-[#61748E] justify-end gap-4 px-2 py-2 mt-6">
        {currentSlide > 1 && (
          <button
            className="bg-[#869ab6] text-white px-6 py-2 rounded-full"
            onClick={handlePrevious}
          >
            Previous
          </button>
        )}
        {currentSlide < 3 && (
          <button
            className="bg-[#0fa9a6] text-white px-10 py-2 rounded-full"
            onClick={handleNext}
          >
            Next
          </button>
        )}
      </div>

      {fileUrl.url !== "" && (
        <div
          className="absmiddle"
          style={{
            width: "100vw",
            height: "100vh",
            zIndex: 999999,
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <XIcon
            className="absolute right-4 top-2 z-10 cursor-pointer bg-red-500 text-white"
            style={{ right: "115px" }}
            onClick={handleCloseFile}
          />
          <div
            className="absmiddle bg-white border py-9"
            style={{
              width: "calc(100vw - 200px)",
              height: "100vh",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <DocumentViewer file={fileUrl.url} name={fileUrl.name} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingView;
